import { useEffect, useState } from "react";
import { ExternalLink, Landmark, Megaphone, Newspaper } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import api from "@/api";
import { ASSO, formatDate, formatDateTime } from "@/constants";

const SOURCE_BADGE = {
  cetf: "bg-emerald-600 text-white",
  toury: "bg-sky-600 text-white",
  janville: "bg-amber-500 text-slate-900",
  beauce: "bg-violet-600 text-white",
  "service-public": "bg-blue-50 text-blue-800 border-blue-200 border",
  education: "bg-emerald-50 text-emerald-800 border-emerald-200 border",
  eduscol: "bg-teal-50 text-teal-800 border-teal-200 border",
  caf: "bg-sky-50 text-sky-800 border-sky-200 border",
  "3018": "bg-rose-50 text-rose-800 border-rose-200 border",
  jpe: "bg-violet-50 text-violet-800 border-violet-200 border",
};
const badgeClass = (s) => SOURCE_BADGE[s] || "bg-slate-50 text-slate-700 border-slate-200 border";
const mediaSrc = (u) => (u ? (u.startsWith("http") ? u : `${process.env.REACT_APP_BACKEND_URL}${u}`) : "");

export default function FamilyLife() {
  const [news, setNews] = useState(null);
  const [local, setLocal] = useState(null);

  useEffect(() => {
    document.title = `Vie des familles | ${ASSO.name}`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute("content",
        "Vie des familles à Toury : actualités de l'association CETF, panneaux locaux et infos institutionnelles (Service-Public, 3018, Je protège mon enfant) pour les parents de l'école Julien Mercier.");
    }
    api.get("/ticker").then((r) => setLocal(Array.isArray(r.data) ? r.data : [])).catch(() => setLocal([]));
    api.get("/family-news").then((r) => setNews(Array.isArray(r.data) ? r.data : [])).catch(() => setNews([]));
  }, []);

  return (
    <div data-testid="family-life-page">
      <div className="home-watermark" style={{ backgroundImage: "url(/assets/logo-tree.png)" }} aria-hidden="true" />

      <section className="relative z-[1] bg-gradient-to-b from-emerald-50/80 to-white py-12 sm:py-16 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 max-w-3xl">
          <p className="text-xs font-semibold uppercase tracking-widest text-emerald-600 mb-2">CETF · Toury</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900" data-testid="family-life-title">Vie des familles</h1>
          <p className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed">
            Toutes les infos utiles aux parents de l'école Julien Mercier : les annonces de l'association et des
            panneaux locaux, puis les actualités institutionnelles (scolarité, aides, numérique, prévention).
            Une page à partager aux familles.
          </p>
        </div>
      </section>

      <section className="relative z-[1] py-10 sm:py-14 bg-white" data-testid="family-life-local-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-5">
            <Megaphone className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg sm:text-xl font-bold text-slate-900">Le fil local</h2>
          </div>
          {local === null ? (
            <p className="text-sm text-slate-400">Chargement…</p>
          ) : local.length === 0 ? (
            <p className="text-sm text-slate-500 bg-slate-50 rounded-xl border border-slate-200 p-5" data-testid="family-life-local-empty">
              Aucune annonce locale pour le moment — revenez bientôt.
            </p>
          ) : (
            <div className="space-y-3">
              {local.map((it) => (
                <Card key={it.id} className="border-slate-200" data-testid={`local-item-${it.id}`}>
                  <CardContent className="p-4 flex items-center gap-4">
                    {it.media_url && it.media_type === "image" && (
                      <img src={mediaSrc(it.media_url)} alt=""
                        className="h-14 w-14 rounded-lg object-cover shrink-0 border border-slate-200" />
                    )}
                    {it.media_url && it.media_type === "video" && (
                      <video src={mediaSrc(it.media_url)} muted loop playsInline autoPlay
                        className="h-14 w-20 rounded-lg object-cover shrink-0 border border-slate-200" />
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm sm:text-base font-medium text-slate-800 leading-snug">{it.text}</p>
                      {it.created_at && <p className="text-xs text-slate-400 mt-1">{formatDateTime(it.created_at)}</p>}
                    </div>
                    <span className={`shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-full ${badgeClass(it.source)}`}
                      data-testid={`local-badge-${it.id}`}>
                      {it.source_label}
                    </span>
                    {it.link && (
                      <a href={it.link} target="_blank" rel="noopener noreferrer"
                        className="shrink-0 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:underline"
                        data-testid={`local-link-${it.id}`}>
                        Voir <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="relative z-[1] py-10 sm:py-14 bg-slate-50/70 border-t border-slate-100" data-testid="family-life-news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2.5 mb-1.5">
            <Newspaper className="h-5 w-5 text-emerald-600" />
            <h2 className="text-lg sm:text-xl font-bold text-slate-900">Actualités des familles</h2>
          </div>
          <p className="text-xs text-slate-400 mb-5">Sélection automatique auprès des institutions — renouvelée toutes les six heures.</p>
          {news === null ? (
            <p className="text-sm text-slate-400">Chargement…</p>
          ) : news.length === 0 ? (
            <p className="text-sm text-slate-500 bg-white rounded-xl border border-slate-200 p-5" data-testid="family-life-news-empty">
              Aucune actualité disponible pour le moment.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {news.map((it) => (
                <Card key={it.id} className="border-slate-200 overflow-hidden flex flex-col" data-testid={`news-card-${it.id}`}>
                  {it.image ? (
                    <img src={it.image} alt="" loading="lazy" referrerPolicy="no-referrer"
                      className="h-32 w-full object-cover" />
                  ) : (
                    <div className="h-20 bg-gradient-to-br from-emerald-50 to-sky-50 flex items-center justify-center">
                      <Landmark className="h-7 w-7 text-emerald-300" />
                    </div>
                  )}
                  <CardContent className="p-4 flex flex-col flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badgeClass(it.source)}`}
                        data-testid={`news-badge-${it.id}`}>
                        {it.source_label}
                      </span>
                      {it.date && <span className="text-xs text-slate-400">{formatDate(it.date)}</span>}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 leading-snug flex-1">{it.title}</h3>
                    {it.summary && (
                      <p className="text-xs text-slate-600 leading-relaxed mt-1.5 line-clamp-3">{it.summary}</p>
                    )}
                    <a href={it.link} target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:underline mt-3"
                      data-testid={`news-link-${it.id}`}>
                      Lire la suite <ExternalLink className="h-3 w-3" />
                    </a>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
